import type { MoveChoice } from '@/schemas/move';

/**
 * Vincoli archetipo su `MoveChoice` (FI-32).
 *
 * Le opzioni di `choices` shorthand o `choiceGroups` possono dichiarare
 * **strutturalmente**:
 *  - `requiresArchetype: string` — opzione disponibile **solo** per quel
 *    archetipo (es. Tradizione Sacra *Natura* del Paladino → solo Elfo, FI-2).
 *  - `freeForArchetype: string` — opzione **gratis** (non conta nel
 *    `pickCount` del gruppo) per quel archetipo, ma disponibile anche agli
 *    altri (es. miglioramento «truce» del Vincolo della Lama → gratis per
 *    Warlock Mezz'orco; beneficio «immunità ai veleni» del Contratto →
 *    gratis per Warlock Nano).
 *
 * Prima di FI-32, l'unico caso noto (`paladino.ordine-sacro::natura`) era
 * mappato in una `Record` hardcoded; ora la regola vive direttamente sulla
 * `MoveChoice` nei dati di gioco.
 */

/**
 * `true` se l'opzione è disponibile per un PG con l'archetipo dato.
 *
 * - Se `option.requiresArchetype` non è definito → sempre disponibile.
 * - Se `option.requiresArchetype === archetypeId` → disponibile.
 * - Altrimenti → non disponibile (la UI nasconde l'opzione e il predicate
 *   non la conta).
 */
export function isMoveChoiceAvailable(
  option: MoveChoice,
  archetypeId: string | undefined,
): boolean {
  if (!option.requiresArchetype) return true;
  return option.requiresArchetype === archetypeId;
}

/**
 * `true` se l'opzione è **gratis** per un PG con l'archetipo dato (cioè
 * non conta nel `pickCount` del gruppo). La UI marca l'opzione come
 * "(gratis)" e la mantiene sempre selezionata + non rimovibile.
 */
export function isMoveChoiceFree(option: MoveChoice, archetypeId: string | undefined): boolean {
  if (!option.freeForArchetype) return false;
  return option.freeForArchetype === archetypeId;
}
